<template>
  <div class="program-card" :class="{'selected': isSelected, 'disabled-card': !canDelete}">
    <div
        v-if="showDelete && !isLoading"
        class="checkbox-wrapper"
        :class="{'disabled': !canDelete}"
        @click.stop="handleSelect"
    >
      <img
          v-if="!isSelected"
          src="@/assets/images/checkbox-circle.svg"
          alt="Select"
          class="checkbox-icon"
      />
      <img
          v-else
          src="@/assets/images/checkboxchecked-circle.svg"
          alt="Selected"
          class="checkbox-icon"
      />
    </div>

    <div class="program-card-header">
      <img :src="image" v-if="!isLoading" class="header-image" />
      <free-style-shimmer
          class="header-image"
          :is-loading="isLoading"
          color="#EAECF0"
          height="160px"
          width="100%"
          border-radius="0"
      />
      <router-link :to="to">
        <button class="details-button" v-if="!isLoading">{{ $t('details') }}</button>
      </router-link>

      <div class="status-tag" :class="isPublish ? 'Published' : 'Draft'">
        <div class="status-dot" :class="isPublish ? 'Published' : 'Draft'"></div>
        <span>{{isPublish ? $t('published') : $t('draft')}}</span>
      </div>
    </div>

    <div class="program-card-body">
      <h2 v-if="!isLoading">
        {{ title }}
      </h2>
      <free-style-shimmer
          :is-loading="isLoading"
          height="12px"
          width="120px"
          border-radius="50px"
          color="#EAECF0"
      />

      <h4 v-if="!isLoading">
        {{ description }}
      </h4>

      <div class="info-row" v-if="!isLoading">
        <span class="info-tag" v-if="companiesAssignedCount > 1">{{ $t('companies_count', {companiesCount: companiesAssignedCount}) }}</span>
        <span class="info-tag">{{ $t('user_count', {userCount: userAssignedCount}) }}</span>
      </div>

      <div class="progress-container" v-if="!isLoading && showProgress">
        <span class="progress-percentage" :class="progressColorClass">%{{ roundedProgress }}</span>
        <div class="progress-bar">
          <div class="progress-fill" :class="progressColorClass" :style="{ width: roundedProgress + '%' }"></div>
        </div>
      </div>

      <free-style-shimmer
          class="paragraph-shimmer top"
          :is-loading="isLoading"
          height="8px"
          width="170px"
          border-radius="50px"
          color="#EAECF0"
      />

      <free-style-shimmer
          class="paragraph-shimmer"
          :is-loading="isLoading"
          height="8px"
          width="140px"
          border-radius="50px"
          color="#EAECF0"
      />

      <router-link v-for="item in tests"  v-if="!isLoading" :to="`/test/${item.testId}`">

        <div class="tag-column" v-if="!isLoading">
          <span class="program-tag" v-for="tag in tags">{{ tag }}</span>
        </div>

        <div class="program-footer" v-if="!isLoading">
          <div class="program-info">
            <img src="@/assets/images/datepicker.svg" class="program-info-icon" />
            <span class="program-info-text">
            {{ formatDate(startAt) }} - {{ formatDate(finishAt) }}
          </span>
          </div>
          <div class="dot"></div>

          <div class="program-info" v-if="remaining.isPast === 0">
            <img
                src="@/assets/images/clockOutlineRed.svg"
                class="program-info-icon"
            />
            <span class="program-info-text red">{{$t('time_is_up')}}</span>
          </div>

          <div class="program-info" v-if="remaining.isPast === 1">
            <img
                src="@/assets/images/clockOutlineGreen.svg"
                class="program-info-icon"
            />
            <span class="program-info-text green">{{ remaining.time }}</span>
          </div>

          <div class="program-info" v-else-if="remaining.isPast === 2">
            <img
                src="@/assets/images/clockOutlineYellow.svg"
                class="program-info-icon"
            />
            <span class="program-info-text yellow">{{ remaining.time }}</span>
          </div>
        </div>

      </router-link>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    isLoading: {
      type: Boolean,
      default: true,
    },
    image: {
      type: String,
      default: "",
    },
    title: {
      type: String,
      default: "Program Name",
    },
    description: {
      type: String,
      default:
          "Lorem ipsum dolor sit amet consectetur. Elit suspendisse habitant vitae...",
    },
    tags: {
      type: Array,
      default: () => ["Relationship", "Marriage"],
    },
    remainingDays: {
      type: Number,
      default: 0,
    },
    remaining: {
      type: Array,
      default: () => ["isPast", "time"],
    },
    assignedAt: {
      type: String,
      default: "",
    },
    startAt: {
      type: String,
      default: "",
    },
    finishAt: {
      type: String,
      default: "",
    },
    isPublish: {
      type: Boolean,
      default: false,
    },
    companiesAssignedCount: {
      type: Number,
      default: 0,
    },
    userAssignedCount: {
      type: Number,
      default: 0,
    },
    to: {
      type: String,
      default: "/programs",
    },
    showDelete: {
      type: Boolean,
      default: false,
    },
    isSelected: {
      type: Boolean,
      default: false,
    },
    onSelect: {
      type: Function,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    assignedType: {
      type: String,
      default: 'company',
    },
    progress: {
      type: Number,
      default: 0,
    },
    showProgress: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    roundedProgress() {
      return Math.round(this.progress || 0);
    },
    progressColorClass() {
      const p = this.roundedProgress;
      if (p >= 80) return 'high';
      if (p >= 40) return 'medium';
      return 'low';
    }
  },
  methods: {
    formatDate(dateStr) {
      try {
        const date = new Date(dateStr);
        const options = { year: "numeric", month: "short", day: "numeric" };
        return date.toLocaleDateString("en", options);
      } catch (e) {
        return "-";
      }
    },
    handleSelect() {
      if (this.onSelect) {
        this.onSelect();
      }
    },
  },
};
</script>

<style scoped>
.program-card {
  position: relative;
  width: 100%;
  background-color: #fff;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
}

.program-card.selected {
  border-color: #3DA33E;
  background-color: #f8fff8;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(61, 163, 62, 0.15);
}

.program-card.disabled-card {
  opacity: 0.7;
}

.checkbox-wrapper {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 100;
  width: 24px;
  height: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  transition: all 0.2s ease;
}

.checkbox-wrapper:hover {
  background-color: rgba(255, 255, 255, 0.9);
  transform: scale(1.1);
}

.checkbox-wrapper.disabled {
  cursor: not-allowed !important;
  opacity: 0.5;
}

.checkbox-wrapper.disabled:hover {
  transform: none !important;
}

.checkbox-icon {
  width: 18px;
  height: 18px;
}

.program-card-header {
  position: relative;
  overflow: hidden;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.details-button {
  visibility: hidden;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 99;
  background-color: #4a68f8;
  border-radius: 9px;
  padding: 5px 20px;
  border: none;
  outline: none;
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #ffffff;
  opacity: 0;
  transition: opacity 0.2s, visibility 0.2s;
}

.program-card:hover .details-button {
  opacity: 1;
  visibility: visible;
}

.program-card:hover .header-image {
  filter: brightness(0.9);
}

.program-card:hover {
  box-shadow: 0 4px 20px rgba(16, 24, 40, 0.1);
}

.header-image {
  width: 100%;
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  height: 160px;
  object-fit: cover;
  transition: filter 0.2s;
}

.program-card-body {
  padding: 15px;
  display: flex;
  flex-direction: column;
  flex: 1;
}

h2 {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #101828;
  margin-bottom: 10px;
}

h4 {
  font-family: euclid_regular, sans-serif;
  color: #667085;
  font-size: 12px;
  margin-bottom: 15px;
}

.program-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #101828;
  background-color: #e9eaef;
  border-radius: 8px;
  padding: 3px 12px;
}

.program-info {
  display: flex;
  flex-direction: row;
  gap: 5px;
  align-items: center;
}

.program-info-text {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #667085;
}

.program-info-text.red {
  color: #ff2121;
}

.program-info-text.yellow {
  color: #f1c40f;
}

.program-info-text.green {
  color: #088701;
}

.dot {
  height: 4px;
  width: 4px;
  border-radius: 50%;
  background-color: #d0d5dd;
}

.program-footer {
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
  margin-top: 20px;
}

.program-info-icon {
  width: 24px;
  height: 24px;
}

.paragraph-shimmer.top {
  margin-top: 15px;
}

.paragraph-shimmer {
  margin-bottom: 5px;
}

.tag-column {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 15px;
}

.status-tag {
  position: absolute;
  top: 10px;
  left: 10px;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #040c2d;
  border-radius: 8px;
  padding: 3px 15px;
  margin-bottom: 10px;
  display: flex;
  gap: 5px;
  align-items: center;
}

.status-dot {
  height: 8px;
  width: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-tag.Draft {
  background-color: #fad9c6;
}

.status-tag.Published {
  background-color: #ddfbde;
}

.status-dot.Draft {
  background-color: #f09460;
}

.status-dot.Published {
  background-color: #3da33e;
}

.programs-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 10px;
}

@media (max-width: 1024px) {
  .programs-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .programs-row {
    grid-template-columns: 1fr;
  }
}

.info-tag {
  font-family: euclid_regular, sans-serif;
  font-size: 10px;
  color: #040C2D;
  border-radius: 8px;
  padding: 3px 15px;
  margin-bottom: 12px;
  display: flex;
  gap: 5px;
  align-items: center;
  background-color: #F2F4F7;
}

.info-row {
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-top: auto;
}

.progress-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: auto -15px -15px -15px;
}

.progress-percentage {
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  font-weight: 700;
  align-self: flex-end;
  padding-right: 15px;
}

.progress-percentage.low {
  color: #dc2626;
}

.progress-percentage.medium {
  color: #d97706;
}

.progress-percentage.high {
  color: #16a34a;
}

.progress-bar {
  position: relative;
  width: 100%;
  height: 20px;
  background: #f5f5f5;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 16px;
}

.progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-bottom-left-radius: 16px;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.progress-fill.low {
  background: linear-gradient(90deg, #ef4444 0%, #f87171 100%);
}

.progress-fill.medium {
  background: linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%);
}

.progress-fill.high {
  background: linear-gradient(90deg, #22c55e 0%, #4ade80 100%);
}
</style>
